import { Api } from '../typings/api';
import store from '../store';
import Utils, { HttpContentType, HttpHeader } from '.';
import userAgentUtils from './userAgent';
import UrlUtils from './url';
import { MultiAppAuthenticationInfosState, MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { Server, AuthMethod, ASP_SERVERS } from '../containers/MultiAppAuthentication/constants';
import { Security } from '../containers/Authentication/reducer';
import { StoreState } from '../reducers';
import { ProfilePageFields, UserAvatarState } from '../containers/ProfilePage/reducer';
import { getIsAccountManagerOrAdmin } from '../containers/ProfilePage/selectors';
import { parseHtmlString } from '../containers/Editor/utils/html-parse-serialize';

export default class ApiUtils {

	public static isDuplicateFileNameError(errorText: string): boolean {
		const DUPLICATE_NAME_ERROR = 'DUPLICATE_NAME';
		return errorText.includes(DUPLICATE_NAME_ERROR);
	}

	private static get galleryConfig(): Api.Config {
		if (!store) {
			console.error('ApiUtils.galleryConfig() is called before store is created');
		}
		const { galleryInfos } = store.getState();
		if (!galleryInfos.isConfigLoaded) {
			console.error('ApiUtils.galleryConfig() is called before galleryInfos.config is loaded');
		}
		return galleryInfos.config;
	}

	private static get multiAppAuthenticationInfos(): MultiAppAuthenticationInfosState {
		if (!store) {
			console.error('ApiUtils.multiAppAuthenticationInfos() is called before store is created');
		}
		const { multiAppAuthenticationInfos } = store.getState();
		return multiAppAuthenticationInfos;
	}

	public static loginToAsp = (credentials: MultiAppAuthenticationCredentials, shouldSaveCredentials: boolean): Promise<Api.AppLoginResponse> => {
		const { galleryInfos: { config } } = store.getState();

		if (credentials.method === AuthMethod.Webpublication) {
			const url = UrlUtils.getLoginToAspUrl(config);
			const { login, password, company } = credentials;
			const data = {
				login,
				password,
				rememberMe: shouldSaveCredentials,
				...(company ? { org: company } : {}),
			};

			return Utils.fetchJson(
				url,
				'POST',
				true,
				true,
				null,
				Utils.encodeFormPayload(data),
				HttpContentType.URLENCODED
			);
		} else {
			const url = UrlUtils.getOrgLoginUrl(config);
			const { company, token } = credentials;
			const data = {
				org: company,
				token,
			};

			return Utils.fetchJson(url, 'POST', true, true, null, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
		}
	}

	public static freeUrlAppLogin = (): Promise<Api.AppLoginResponse> => {
		const { galleryInfos: { config } } = store.getState();
		const url = UrlUtils.getFreeUrlAppLoginUrl(config);
		const accessToken = Utils.getQueryParamFromUrl('wpAccessToken');

		return Utils.fetchJson(url, 'POST', true, true, null, Utils.encodeFormPayload({ accessToken }), HttpContentType.URLENCODED);
	}

	public static refreshSession = (requestId?: string): Promise<Api.AppLoginResponse> => {
		const { galleryInfos: { config } } = store.getState();
		const url = UrlUtils.getRefreshSessionUrl(config);

		return Utils.fetchJson(url, 'GET', true, true, null, undefined, undefined, true, true, false, () => undefined, requestId);
	}

	public static get multiAppAuthenticationServer(): Server {
		return this.multiAppAuthenticationInfos.server as Server;
	}

	public static get multiAppAuthenticationToken(): string | null {
		const { credentials: { token = null } = {} } = this.multiAppAuthenticationInfos;
		return token;
	}

	public static get multiAppAuthenticationLastApiCallTimestamp(): number | null {
		const { credentials: { lastApiCallTimestamp = null } = {} } = this.multiAppAuthenticationInfos;
		return lastApiCallTimestamp;
	}

	public static get token(): string | null {
		if (Utils.isEmbedMode()) {
			return Utils.getQueryParamFromUrl('token');
		}

		return ApiUtils.multiAppAuthenticationToken;
	}

	public static get lastApiCallTimestamp(): number | null {
		const lastApiCallTimestamp = ApiUtils.multiAppAuthenticationLastApiCallTimestamp;

		return lastApiCallTimestamp ? +lastApiCallTimestamp : null;
	}

	public static logoutFromAsp = (): Promise<{}> => {
		const { galleryConfig } = ApiUtils;
		return Utils.fetchText(UrlUtils.getLogoutFromAspUrl(galleryConfig), 'POST');
	}

	public static logoutFromApplication = (): Promise<{}> => {
		const { galleryConfig } = ApiUtils;

		// there can be different application urls from ASP_SERVERS and from galleryConfig
		// in this case logout must be called on both of them
		const multiAppAuthenticationAppUrl = ASP_SERVERS[ApiUtils.multiAppAuthenticationServer].driveUrl;
		const isSameAppUrl = Utils.isSameUrl(galleryConfig.applicationUrl, multiAppAuthenticationAppUrl);

		const promises = [
			Utils.fetchText(UrlUtils.getLogoutFromApplication(galleryConfig), 'POST'),
		];

		if (!isSameAppUrl) {
			promises.push(Utils.fetchText(UrlUtils.getLogoutFromApplication(galleryConfig, true), 'POST'));
		}

		return Promise.all(promises);
	}

	public static createAspDemoAccount = (email: string, password: string, phone: string): Promise<Api.CustomerAccount> => {
		const { galleryConfig } = ApiUtils;
		const loginData = { email, password, phone };

		return Utils.fetchJson<Api.CustomerAccount>(UrlUtils.getCreateDemoAccountUrl(galleryConfig), 'POST', true, true, loginData)
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	public static getOrgAuthMethods = (company: string): Promise<Api.AuthMethodsResponse> => {
		const { galleryConfig } = ApiUtils;

		return Utils.fetchJson<Api.AuthMethodsResponse>(UrlUtils.getAuthMethodsUrl(galleryConfig), 'GET', true, true, { org: company })
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	// TODO: change return type of authenticate
	public static authenticate = (user: string, pass: string): Promise<{} | void> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getLoginUrl(ApiUtils.galleryConfig, user, pass), 'GET', isAsync);
	}

	public static async checkSession(): Promise<{}> {
		return Utils.fetchJson(UrlUtils.getCheckSessionUrl(ApiUtils.galleryConfig), 'GET');
	}

	public static getSecurityHeaderIfNeeded = (preventSecurityHeader: boolean): HttpHeader[] => {
		const { authentication } = store.getState();
		const { mode } = authentication.userInfos;
		const isSamlAuthenticationType = mode && mode.method === Security.Saml;
		return isSamlAuthenticationType && !preventSecurityHeader
			? [{
				key: 'Access-Control-Allow-Credentials',
				value: 'true',
			}]
			: [];
	}

	public static getSecurityType = () => {
		const galleryConfig = ApiUtils.galleryConfig;
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.promiseXHR('GET', url, [], isAsync)
			.then(response => {
				const securityType = response.getResponseHeader('Security') as Security;
				const samlUrl = response.getResponseHeader('SAML_URL') || '';

				return securityType
					? Promise.resolve({securityType, samlUrl})
					: Promise.resolve(undefined);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	public static getTemplate = (server?: Server): Promise<Api.Template> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getTemplateUrl('template.json', ApiUtils.galleryConfig, server), 'GET', isAsync);
	}

	public static getCustomCSS = (server?: Server): Promise<string> => {
		const isAsync = !userAgentUtils.isIOSApp();
		return Utils.fetchText(UrlUtils.getTemplateUrl('custom.css', ApiUtils.galleryConfig, server), 'GET', isAsync);
	}

	public static getConfig = (clientId: number, galleryId: number): Promise<Api.Config> => {
		return Utils.fetchJson(UrlUtils.getConfigUrl(ApiUtils.multiAppAuthenticationServer, clientId, galleryId), 'GET', true, true);
	}

	public static getStatistics = (): Promise<string> => {
		return Utils.fetchText(UrlUtils.getStatisticsUrl(ApiUtils.galleryConfig));
	}

	public static getChildren = (driveItemId?: number): Promise<Api.Outcoming> | void => {
		const galleryConfig = ApiUtils.galleryConfig;

		const resolvedDriveItemId = driveItemId || galleryConfig.applicationGallery;

		return Utils.fetchJson(UrlUtils.getChildrenUrl(ApiUtils.galleryConfig, resolvedDriveItemId), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getChildrenMeta = (): Promise<Api.NestedDriveItems> => {
		const { galleryConfig, galleryConfig: { applicationGallery } } = ApiUtils;
		return Utils.fetchJson(UrlUtils.getChildrenMetaUrl(galleryConfig, applicationGallery), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getAppMeta = (): Promise<Api.NestedAppMeta> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		return Utils.fetchJson(UrlUtils.getAppMetaUrl(galleryConfig, applicationGallery), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getMeta = (driveItemId: number, isAsync: boolean = true, ancestorId?: number): Promise<Api.DriveFolderInfo> => {
		return Utils.fetchJson(UrlUtils.getMetaUrl(ApiUtils.galleryConfig, driveItemId, ancestorId), 'GET', isAsync, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getContentBlock = (driveItemId: number, block: number): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getContentBlockUrl(ApiUtils.galleryConfig, driveItemId, block), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getNewContents = (): Promise<Api.DriveItemContentBlockWithOrderedItems> => {
		return Utils.fetchJson(UrlUtils.getNewContentsUrl(ApiUtils.galleryConfig));
	}

	public static getSearch = (searchString: string): Promise<Api.DriveItemContentBlockWithOrderedItems> => {
		return Utils.fetchJson(UrlUtils.getSearchUrl(ApiUtils.galleryConfig, searchString), 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static getDownloadPDF = (searchString: string): string => {
		return UrlUtils.getDownloadPDFUrl(ApiUtils.galleryConfig, searchString);
	}

	public static getGalleryAsset = (driveItemId: number): Promise<Api.DriveItem> => {
		return Utils.fetchJson(UrlUtils.getGalleryAssetUrl(ApiUtils.galleryConfig, driveItemId), 'GET');
	}

	static buildOutcomingsUrl = (
		driveItemId: number,
		level?: string,
		excludeTypes: Api.OutcomingType[] = [],
		excludeIds: number[] = []
	): string => {
		const url = UrlUtils.getOutcomingsUrl(ApiUtils.galleryConfig, driveItemId);
		const params = new URLSearchParams();

		if (level) {
			params.append('level', level);
		}

		if (excludeTypes.length > 0) {
			excludeTypes.forEach(type => params.append('exclType', type));
		}

		if (excludeIds.length > 0) {
			excludeIds.forEach(id => params.append('exclId', id.toString()));
		}

		return `${url}${Utils.getUrlSeparator(url)}${params.toString()}`;
	}

	public static getOutcomings = (
		driveItemId: number,
		level?: string,
		excludeTypes: Api.OutcomingType[] = [],
		excludeIds: number[] = []
	): Promise<Api.Outcoming> => {
		const finalUrl = ApiUtils.buildOutcomingsUrl(driveItemId, level, excludeTypes, excludeIds);

		return Utils.fetchJson(finalUrl);
	}

	public static getAvailableServers = (): Promise<{ [k: string]: Api.ServerInfos }> => {
		return Utils.fetchJson<Api.GetClosestServerResponse>('https://fr.adminzone-secure.net/services/rest/loginWs/getClosestServer')
			.then(res => res.servers);
	}

	public static shareContent = (requestData: Api.SharedData) => {
		const url = UrlUtils.getShareServiceUrl(ApiUtils.galleryConfig);
		return Utils.fetchText(url, 'POST', true, true, {}, JSON.stringify(requestData)); // requestData needs to be stringified
	}

	public static getCrmAccounts = (): Promise<Api.CrmContact[]> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getCrmAccountsUrl(galleryConfig);
		return Utils.fetchJson<Api.getCrmContactsData>(url, 'GET', true, true)
			.then(crmAccounts => Array.isArray(crmAccounts) ? crmAccounts : []);
	}

	public static createPrivateFolder = (parentGId: number, name: string, resourcesGIds: number[]): Promise<number> => {
		const { galleryConfig, galleryConfig: { clientId } } = ApiUtils;

		if (parentGId) {
			const requestData: Api.CreateFolderData = {
				driveLabel: name,
				clientId,
				parentGId,
				resourcesGIds: resourcesGIds,
			};
			const url = UrlUtils.getCreatePrivateFolderUrl(galleryConfig);
			return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(requestData), HttpContentType.URLENCODED)
				.then(newFolderIdString => parseInt(newFolderIdString, 10));
		}

		return Promise.reject();
	}

	public static createExternalLink = (folderGId: number, label: string, linkUrl: string): Promise<number> => {
		const url = UrlUtils.getCreateExternalLinkUrl(ApiUtils.galleryConfig);
		const data = {
			clientId: ApiUtils.galleryConfig.clientId,
			folderGId,
			label,
			url: linkUrl,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getIndesignEditorData = (clientId: number, publicationGId: number, allowPreview: boolean): Promise<Api.IndesignEditorData> => {
		const dataInUrl = {
			clientId,
			publicationGId,
			allowPreview,
		};

		return Utils.fetchJson(UrlUtils.getIndesignEditorDataUrl(ApiUtils.galleryConfig), 'GET', true, true, dataInUrl);
	}

	public static getHtmlEditorData = (clientId: number, pageGId: number): Promise<Api.HtmlEditorData> => {
		const dataInUrl = {
			clientId,
			pageGId,
		};

		return Utils.fetchJson(UrlUtils.getHtmlEditorDataUrl(ApiUtils.galleryConfig), 'GET', true, true, dataInUrl);
	}

	public static getEditorResource = (resourceId: number | undefined, relPath: string | undefined): Promise<string> => {
		const url = UrlUtils.getEditorResourceUrl(ApiUtils.galleryConfig, resourceId, relPath);

		return Utils.fetchText(url, 'GET', true, true, { token: ApiUtils.token });
	}

	public static saveIndesignEditorPage = (clientId: number, resourceGId: number, pageNum: number, htmlContent: string, requestId?: string): Promise<string> => {
		const url = UrlUtils.getSaveIndesignEditorPageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			pageNum,
			htmlContent,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED, true, true, false, () => undefined, requestId);
	}

	public static getResourceStyle = async (resourceUrl: string): Promise<string> => {
		try {
			const url = `${resourceUrl}?token=${ApiUtils.token}`;
			const response = await fetch(url);

			if (!response.ok) {
				console.error('Error fetching resource style:', response.statusText);
				return '';
			}

			const text = await response.text();
			const doc = parseHtmlString(text);
			const styleElement = doc.querySelector('style');
			let styleContent = '';

			if (styleElement) {
				styleContent = styleElement.textContent || '';
			} else {
				console.warn('No "<style>" element found in the resource.');
			}

			return styleContent;
		} catch (error) {
			console.error('Error fetching or parsing resource style:', error);
			return '';
		}
	}

	public static saveEditedAccessiblePage = (clientId: number, resourceGId: number, pageNum: number, htmlContent: string, requestId?: string): Promise<string> => {
		const url = UrlUtils.getSaveEditedAccessiblePageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			pageNum,
			htmlContent,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED, true, true, false, () => undefined, requestId);
	}

	public static saveIndesignEditorConfigs = (clientId: number, resourceGId: number, configs: string, requestId?: string): Promise<string> => {
		const url = UrlUtils.getSaveIndesignEditorConfigsUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId: resourceGId,
			configsJsonContent: configs,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED, true, true, false, () => undefined, requestId);
	}

	public static saveIndesignEditorThemesCss = (clientId: number, publiGId: number, cssContent: string): Promise<number[]> => {
		const url = UrlUtils.getSaveIndesignEditorThemesCssUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			cssContent,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static saveHtmlEditorPage = (clientId: number, pageGId: number, htmlContent: string): Promise<string> => {
		const url = UrlUtils.getSaveHtmlEditorPageUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			pageGId,
			htmlContent,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static importFile(
		clientId: number,
		folderId: number,
		file: File,
		onProgress?: (progress: ProgressEvent) => void,
		requestId?: string,
		isOptimizedImageImport?: boolean,
		isPublicationDependency?: boolean,
	): Promise<Api.ImportFileData> {
		const url = UrlUtils.getImportFileUrl(ApiUtils.galleryConfig, file.name, isOptimizedImageImport);
		const formData = new FormData();
		formData.append('clientId', clientId.toString());
		formData.append('folderGId', folderId.toString());
		formData.append('file', file);
		formData.append('filename', file.name);

		if (isPublicationDependency) {
			formData.append('dependency', 'true');
		}

		return Utils.fetchJson(url, 'POST', true, true, null, formData, undefined, true, true, false, onProgress, requestId);
	}

	public static createCombo = (clientId: number, resourceGId: number, resourcesGIds: number[], newLabel: string): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateComboUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId: resourceGId,
			resourcesGIds,
			newLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static createComboWithExternalSpreads = (clientId: number, resourceGId: number, sourceGId: number, pages: number[]): Promise<Api.ComboWithExternalSpreadsData> => {
		const url = UrlUtils.getCreateComboWithExternalSpreadsUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId: resourceGId,
			srcPubliGId: sourceGId,
			pages,
		};

		return Utils.fetchJson<Api.ComboWithExternalSpreadsData>(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getPublicationProgress = async (
		clientId: number,
		publicationGId: number,
		onUpdate: (percentage: number) => void,
		onError: () => void,
		onFinish: () => void,
		shouldCancel?: (state: StoreState) => boolean,
		onCancel?: () => void,
	): Promise<void> => {
		const GET_PROGRESS_DELAY: number = 2000;
		let getProgressTimer: number;

		const url = UrlUtils.getPublicationProgressUrl(ApiUtils.galleryConfig);
		const dataInUrl = {
			clientId,
			publicationGId,
		};

		const getProgress = async (): Promise<void> => {
			try {
				if (shouldCancel && shouldCancel(store.getState())) {
					clearInterval(getProgressTimer);

					if (onCancel) {
						onCancel();
					}
				}

				const { status, percentage } = await Utils.fetchJson<Api.PublicationProgressData>(url, 'GET', true, true, dataInUrl);

				if (status === Api.AssetItemStatus.PROCESSING) {
					onUpdate(percentage);
				} else {
					clearInterval(getProgressTimer);

					if (status === Api.AssetItemStatus.LIVE) {
						onFinish();
					}

					if (status === Api.AssetItemStatus.ERROR) {
						onError();
					}
				}
			} catch (error) {
				onError();
				clearInterval(getProgressTimer);
			}
		};

		getProgressTimer = window.setInterval(() => {
			getProgress();
		}, GET_PROGRESS_DELAY);
	}

	public static getPublicationTemplates = (): Promise<Api.PublicationTemplatesData> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		const url = UrlUtils.getPublicationTemplatesUrl(galleryConfig, applicationGallery);

		return Utils.fetchJson(url, 'GET', true, true, ApiUtils.getIncludeAllResourcesParam());
	}

	public static setAsTemplate = (clientId: number, globalId: number): Promise<void> => {
		const url = UrlUtils.getSetAsTemplateUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			globalId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static removeTemplate = (clientId: number, globalId: number): Promise<void> => {
		const url = UrlUtils.getRemoveTemplateUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			globalId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static createPublicationFromMasterTemplate = (clientId: number, applicationGId: number, folderGId: number | undefined, publicationGId: number, newLabel: string): Promise<{
		ancestorId: number;
		resource: Api.AspDriveResourceItem;
	}> => {
		const url = UrlUtils.getCreatePublicationFromMasterTemplateUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			applicationGId,
			folderGId,
			publicationGId,
			newLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static createProposalFromDesktopFile = (
		file: File,
		clientId: number,
		folderId: number,
		onProgress: (progress: ProgressEvent) => void,
		requestId: string,
): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateProposalFromDesktopFileUrl(ApiUtils.galleryConfig);
		const formData = new FormData();
		formData.append('clientId', clientId.toString());
		formData.append('parentDriveGId', folderId.toString());
		formData.append('file', file);
		formData.append('filename', file.name);
		formData.append('creationSource', Api.CreationSource.DESKTOP);

		return Utils.fetchJson(url, 'POST', true, true, null, formData, undefined, true, true, false, onProgress, requestId);
	}

	public static createProposalFromDriveItem = (
		folderId: number,
		clientId: number,
		resourceGId: number,
		newLabel: string,
		requestId: string
	): Promise<Api.AspDriveResourceItem> => {
		const url = UrlUtils.getCreateProposalFromDriveItemUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			newLabel,
			parentDriveGId: folderId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED, true, true, false, undefined, requestId);
	}

	public static sharePublication = (clientId: number, resourceGId: number, name: string): Promise<Api.SharePublicationData> => {
		const url = UrlUtils.getSharePublicationUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			name,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static renameResource = (clientId: number, resourceGId: number, resourceLabel: string): Promise<Api.RenameResourceData> => {
		const url = UrlUtils.getRenameResourceUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
			resourceLabel,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static trashResources = (clientId: number, resourcesGIds: number[]): Promise<Api.TrashResourcesData> => {
		const url = UrlUtils.getTrashResourcesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourcesGIds,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static removePublicationPages = (clientId: number, publiGId: number, pages: number[]): Promise<void> => {
		const url = UrlUtils.getRemovePublicationPagesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			pages,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static movePublicationPages = (clientId: number, publiGId: number, pages: number[], newPosition: number): Promise<void> => {
		const url = UrlUtils.getMovePublicationPagesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			pages,
			newPosition,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static duplicatePublicationPages = (clientId: number, publiGId: number, pages: number[]): Promise<void> => {
		const url = UrlUtils.getDuplicatePublicationPagesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
			pages,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static reprocessPublicationThumbs = (clientId: number, publiGId: number): Promise<void> => {
		const url = UrlUtils.getReprocessPublicationThumbsUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			publiGId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getProfileData = (clientId: number): Promise<Api.ProfileData> => {
		const url = UrlUtils.getProfileDataUrl(ApiUtils.galleryConfig);

		return Utils.fetchJson(url, 'GET', true, true, { clientId });
	}

	public static editProfileData = (
		id: number,
		clientId: number,
		fields: ProfilePageFields,
		userAvatar: UserAvatarState,
	): Promise<Api.ProfileData> => {
		const url = UrlUtils.getEditProfileDataUrl(ApiUtils.galleryConfig);
		const data = {} as Api.EditProfileData;

		if (userAvatar.isEdited && !userAvatar.isRemoved) {
			const { config: { name, width, height, x, y } } = userAvatar;
			fields.photoConfig = JSON.stringify({ name, width, height, x, y });
		}

		if (userAvatar.isNew && userAvatar.file) {
			data.profilePic = userAvatar.file;
		}

		if (userAvatar.isRemoved) {
			data.removeProfilePic = 'true';
		}

		data.profileInfo = JSON.stringify({
			id,
			clientId,
			...fields,
		});

		const formData = Object.entries(data).reduce((formData, [key, value]) => {
			formData.append(key, value);
			return formData;
		}, new FormData());

		return Utils.fetchJson(url, 'PUT', true, true, null, formData);
	}

	public static getProposalsByType = (
		clientId: number,
		proposalStatus: Api.ProposalStatus,
		sortOn: string,
		ascending: boolean,
		pageNum: number,
		itemsPerPage: number,
	): Promise<Api.ProposalsByTypeData> => {
		const url = UrlUtils.getProposalsByTypeUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			proposalStatus,
			sortOn,
			ascending,
			pageNum,
			itemsPerPage,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static moveResourcesOrder = (clientId: number, parentDriveGId: number, resourcesGIds: number[], positionBefore: number): Promise<void> => {
		const url = UrlUtils.getMoveResourcesOrderUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			parentDriveGId,
			resourcesGIds,
			positionBefore,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static moveResources = (clientId: number, newParentGId: number, resourcesGIds: number[]): Promise<void> => {
		const url = UrlUtils.getMoveResourcesUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			newParentGId,
			resourcesGIds,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static archiveResources = (clientId: number, resourcesGIds: number[]): Promise<boolean> => {
		const url = UrlUtils.getArchiveResourcesUrl(ApiUtils.galleryConfig);
		const { applicationGallery: appGId } = ApiUtils.galleryConfig;
		const data = {
			clientId,
			resourcesGIds,
			appGId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getNotes = (): Promise<Api.NotesData> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		const url = UrlUtils.getNotesUrl(galleryConfig, applicationGallery);

		return Utils.fetchJson(url, 'GET', true, true);
	}

	public static saveNote = (data: Api.NoteData): Promise<Api.NotesData> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		const url = UrlUtils.getSaveNoteUrl(galleryConfig, applicationGallery);
		const dataToSave = {
			notes: [data],
		};

		return Utils.fetchJson(url, 'POST', true, true, null, JSON.stringify(dataToSave));
	}

	public static removeNote = (id: number): Promise<Api.NotesData> => {
		const { galleryConfig, galleryConfig: { applicationGallery} } = ApiUtils;
		const url = UrlUtils.getSaveNoteUrl(galleryConfig, applicationGallery);
		const dataToSave = {
			deleteIds: [id],
		};

		return Utils.fetchJson(url, 'POST', true, true, null, JSON.stringify(dataToSave));
	}

	public static getResource = (resourceGId: number): Promise<Api.AspDriveResourceItem> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getResourceUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			resourceGId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static getCustomerContext = (): Promise<Api.CustomerContext> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getCustomerContextUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static createComponent(
		clientId: number,
		folderId: number,
		file: File,
		isPublicationDependency?: boolean,
	): Promise<Api.AspDriveResourceItem> {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getCreateComponentUrl(galleryConfig);
		const formData = new FormData();
		formData.append('clientId', clientId.toString());
		formData.append('folderGId', folderId.toString());
		formData.append('file', file);
		formData.append('filename', file.name);

		if (isPublicationDependency) {
			formData.append('dependency', 'true');
		}

		return Utils.fetchJson(url, 'POST', true, true, null, formData);
	}

	public static getPublicationSettings = (publicationGId: number): Promise<Api.PublicationSettings> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getPublicationSettingsUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			publicationGId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static savePublicationSettings = (globalId: number, values: Api.PublicationSettings): Promise<Api.PublicationSettings> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getSavePublicationSettingsUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			globalId,
			...values,
		};

		return Utils.fetchJson(url, 'PUT', true, true, null, JSON.stringify(data), HttpContentType.JSON);
	}

	public static getPublicationPdfSettings = (publicationGId: number): Promise<Api.PublicationPdfSettings> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getPublicationPdfSettingsUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			publicationGId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static savePublicationPdfOption = (publicationGId: number, pdfOption: Api.PublicationPdfOption): Promise<Api.PublicationPdfSettings> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getSavePublicationPdfOptionUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			publicationGId,
			pdfOption,
		};

		return Utils.fetchJson(url, 'POST', true, true, null, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static importPublicationPdf = (
		publicationGId: number,
		file: File | null,
		sourcePublication: { id: number, label: string } | null,
		updateThumbs: boolean,
		onProgress?: (progress: ProgressEvent) => void,
		requestId?: string,
	): Promise<Api.PublicationPdfImportResponse> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getImportPublicationPdfUrl(galleryConfig);
		const formData = new FormData();
		formData.append('clientId', galleryConfig.clientId.toString());
		formData.append('publicationGId', publicationGId.toString());
		formData.append('updateThumbs', updateThumbs.toString());

		if (file) {
			formData.append('file', file);
			formData.append('filename', file.name);
		}

		if (sourcePublication) {
			formData.append('srcPubliGId', sourcePublication.id.toString());
			formData.append('filename', sourcePublication.label);
		}

		return Utils.fetchJson(url, 'POST', true, true, null, formData, undefined, true, true, false, onProgress, requestId);
	}

	public static getPublicationIntegration = (publicationGId: number): Promise<Api.PublicationIntegration> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getPublicationIntegrationUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			publicationGId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static updateOriginalPublication = (tempGId: number, originalGId: number): Promise<{ genericItemLastUpdate: number }> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getUpdateOriginalPublicationUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			tempGId,
			originGId: originalGId,
		};

		return Utils.fetchJson(url, 'POST', true, true, null, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static acquireLock = (clientId: number, resourceGId: number): Promise<void> => {
		const url = UrlUtils.getAcquireLockUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static releaseLock = (clientId: number, resourceGId: number): Promise<void> => {
		const url = UrlUtils.getReleaseLockUrl(ApiUtils.galleryConfig);
		const data = {
			clientId,
			resourceGId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getShareUsers = (): Promise<Api.ShareUser[]> => {
		const {
			galleryConfig,
			galleryConfig: { clientId, applicationGallery },
		} = ApiUtils;
		const url = UrlUtils.getShareUsersUrl(galleryConfig);
		const data = {
			clientId,
			appGId: applicationGallery,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	public static shareWithUser = (resourceGId: number, userId: number): Promise<void> => {
		const {
			galleryConfig,
			galleryConfig: { clientId, applicationGallery },
		} = ApiUtils;
		const url = UrlUtils.getShareWithUserUrl(galleryConfig);
		const data = {
			clientId,
			appGId: applicationGallery,
			resGId: resourceGId,
			userId,
		};

		return Utils.fetchJson(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static changeCoverImage = (resourceGId: number, coverGId: number): Promise<string> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getChangeCoverImageUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			resourceGId,
			coverGId,
		};

		return Utils.fetchText(url, 'POST', true, true, {}, Utils.encodeFormPayload(data), HttpContentType.URLENCODED);
	}

	public static getPublishPrice = (globalId: number): Promise<Api.PublishPrice> => {
		const { galleryConfig } = ApiUtils;
		const url = UrlUtils.getPublishPriceUrl(galleryConfig);
		const data = {
			clientId: galleryConfig.clientId,
			globalId,
		};

		return Utils.fetchJson(url, 'GET', true, true, data);
	}

	// add a special flag for 'editable app' (app with 'Multiple' type and private dir)
	// to get all resources including expired and unpublished
	private static getIncludeAllResourcesParam(): {} {
		const {
			appInfos,
			galleryInfos: { config },
			driveList,
			profile: { role },
		} = store.getState();
		const isAccountManagerOrAdmin = getIsAccountManagerOrAdmin(store.getState());
		const hasPrivateDir = !!Utils.getPrivateDirId(driveList, config);

		if (Utils.isReadOnlyApp(role)) {
			return {};
		}

		if (Utils.isEmbedMode()
			|| (Utils.isMultiApp(appInfos) && (userAgentUtils.isMobileApp || isAccountManagerOrAdmin))) {
			return { excludeNP: false };
		}

		return Utils.isMultiApp(appInfos) && hasPrivateDir ? { excludeNP: false } : {};
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/api.ts